(() => {
  const GUIDE_LINK_SELECTOR = 'a[href*="/pages/measurements-sizing-tips"]';

  function findSizeLabel() {
    return [...document.querySelectorAll('variant-selects .form__label')].find(
      (label) => label.textContent.trim().replace(/:$/, '').toLowerCase() === 'size'
    );
  }

  function splitLegacyRow(line) {
    return line
      .split(/-{2,}/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function buildLegacySizeChart() {
    const description = document.querySelector('.product__description');
    if (!description) return null;

    const sources = [...description.querySelectorAll('p, div')].filter((element) => {
      if (element.closest('details')) return false;
      const compactText = element.textContent.replace(/\s+/g, '').toLowerCase();
      return compactText.includes('sizechart') && element.querySelector('br');
    });

    for (const source of sources) {
      const copy = source.cloneNode(true);
      copy.querySelectorAll('br').forEach((lineBreak) => lineBreak.replaceWith('\n'));
      const lines = copy.textContent
        .split(/\n+/)
        .map((line) => line.replace(/\u00a0/g, ' ').trim())
        .filter(Boolean);
      const headingIndex = lines.findIndex((line) => {
        const cells = splitLegacyRow(line);
        return cells.length >= 2 && cells[0].toLowerCase() === 'size';
      });
      if (headingIndex < 0) continue;

      const headings = splitLegacyRow(lines[headingIndex]);
      const rows = [];
      for (const line of lines.slice(headingIndex + 1)) {
        const cells = splitLegacyRow(line);
        if (cells.length !== headings.length) {
          if (rows.length) break;
          continue;
        }
        rows.push(cells);
      }
      if (rows.length < 2) continue;

      const details = document.createElement('details');
      details.className = 'custom-size-guide-legacy-source';
      const summary = document.createElement('summary');
      summary.textContent = 'S I Z E C H A R T — tap to expand';
      details.appendChild(summary);

      const table = document.createElement('table');
      const thead = table.createTHead();
      const headingRow = thead.insertRow();
      headings.forEach((heading) => {
        const cell = document.createElement('th');
        cell.scope = 'col';
        cell.textContent = heading;
        headingRow.appendChild(cell);
      });
      const tbody = table.createTBody();
      rows.forEach((values) => {
        const row = tbody.insertRow();
        values.forEach((value, index) => {
          const cell = row.insertCell();
          cell.textContent = value;
          if (index === 0) cell.style.fontWeight = '600';
        });
      });
      details.appendChild(table);
      return details;
    }

    return null;
  }

  function findSizeChart() {
    const expandableChart = [...document.querySelectorAll('.product__description details')].find((details) => {
      const summary = details.querySelector('summary');
      const compactText = summary?.textContent.replace(/\s+/g, '').toLowerCase();
      return compactText
        && compactText.includes('sizechart')
        && compactText.includes('taptoexpand')
        && details.querySelector('table');
    });
    return expandableChart || buildLegacySizeChart();
  }

  function copyChartContent(source) {
    const copy = source.cloneNode(true);
    copy.querySelector('summary')?.remove();

    copy.querySelectorAll(GUIDE_LINK_SELECTOR).forEach((link) => {
      link.replaceWith(document.createTextNode(link.textContent.trim()));
    });

    const walker = document.createTreeWalker(copy, NodeFilter.SHOW_TEXT);
    while (walker.nextNode()) {
      walker.currentNode.textContent = walker.currentNode.textContent
        .replace('See Measuring Guide', '')
        .replace('Tip:', '');
    }

    copy.querySelectorAll('strong, b').forEach((element) => {
      if (!element.textContent.trim()) element.remove();
    });

    copy.querySelectorAll('div, p').forEach((element) => {
      if (element.querySelector('div, p, table')) return;
      const noteText = element.textContent.trim().replace(/\s+/g, ' ');
      if (
        noteText.includes('If between sizes, size up for comfort.')
        || noteText.startsWith('Event dresses are non-stretch')
        || noteText.startsWith('Dress chest and waist should be at least')
      ) {
        element.textContent = 'If between sizes, size up for comfort.';
        element.classList.add('custom-size-guide-dialog__comfort-note');
      }
    });

    if (!copy.querySelector('.custom-size-guide-dialog__comfort-note')) {
      const table = copy.querySelector('table');
      if (table) {
        const note = document.createElement('div');
        note.className = 'custom-size-guide-dialog__comfort-note';
        note.textContent = 'If between sizes, size up for comfort.';
        (table.parentElement || table).insertAdjacentElement('afterend', note);
      }
    }

    const fragment = document.createDocumentFragment();
    [...copy.childNodes].forEach((node) => fragment.appendChild(node));
    return fragment;
  }

  async function loadMeasuringGuide(dialog) {
    const content = dialog.querySelector('.custom-size-guide-dialog__content');
    const loading = document.createElement('p');
    loading.className = 'custom-size-guide-dialog__guide-loading';
    loading.textContent = 'Loading Measuring Guide…';
    content.appendChild(loading);

    try {
      const response = await fetch('/pages/measurements-sizing-tips', {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Measuring Guide request failed: ${response.status}`);

      const page = new DOMParser().parseFromString(await response.text(), 'text/html');
      const guide = page.querySelector('.custom-info-guide');
      if (!guide) throw new Error('Measuring Guide content was not found');

      const section = document.createElement('section');
      section.className = 'custom-size-guide-dialog__measuring-guide';
      section.appendChild(guide.cloneNode(true));
      loading.replaceWith(section);
    } catch (error) {
      loading.textContent = 'Measuring Guide is temporarily unavailable.';
      console.error(error);
    }
  }

  function setupUnitToggle(dialog) {
    const measurementCells = [...dialog.querySelectorAll('.custom-size-guide-dialog__content table')]
      .flatMap((table) => {
        const rows = [...table.rows];
        if (rows.length < 2) return [];

        const measurementColumnIndexes = [...rows[0].cells].flatMap((cell, index) => {
          const heading = cell.textContent.trim().toLowerCase();
          return /chest|bust|waist|hip|length|hem|shoulder|top|skirt|total|sleeve/.test(heading)
            ? [index]
            : [];
        });

        return rows.slice(1).flatMap((row) =>
          measurementColumnIndexes
            .map((index) => row.cells[index])
            .filter(Boolean)
        );
      })
      .filter((cell) => {
        const originalText = cell.textContent.trim();
        const measurement = originalText.match(/^(\d+(?:\.\d+)?)(?:\s+(\d+)\/(\d+))?\s*[”"]*\s*[*†‡-]*$/);
        if (!measurement) return false;
        const wholeInches = Number.parseFloat(measurement[1]);
        const numerator = measurement[2] ? Number.parseInt(measurement[2], 10) : 0;
        const denominator = measurement[3] ? Number.parseInt(measurement[3], 10) : 1;
        if (!denominator) return false;
        const inches = wholeInches + numerator / denominator;
        if (!Number.isFinite(inches)) return false;
        cell.dataset.inches = String(inches);
        cell.dataset.inchDisplay = String(
          Math.round((inches + Number.EPSILON) * 100) / 100
        );
        cell.textContent = cell.dataset.inchDisplay;
        return true;
      });

    const buttons = [...dialog.querySelectorAll('.custom-size-guide-dialog__unit-button')];
    const renderUnit = (unit) => {
      measurementCells.forEach((cell) => {
        const inches = Number.parseFloat(cell.dataset.inches);
        const value = unit === 'cm'
          ? String(Math.round((inches * 2.54 + Number.EPSILON) * 10) / 10)
          : cell.dataset.inchDisplay;
        cell.textContent = value;
      });

      buttons.forEach((button) => {
        const isActive = button.dataset.unit === unit;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => renderUnit(button.dataset.unit));
    });

    renderUnit('inch');
  }

  function initialize() {
    if (document.querySelector('.custom-size-guide-trigger')) return true;

    const sizeLabel = findSizeLabel();
    const sizeChart = findSizeChart();
    if (!sizeLabel || !sizeChart) return false;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'custom-size-guide-trigger';
    trigger.textContent = 'Size Guide';
    trigger.setAttribute('aria-haspopup', 'dialog');

    const dialog = document.createElement('dialog');
    dialog.className = 'custom-size-guide-dialog';
    if (window.location.pathname.toLowerCase().includes('asd5000')) {
      dialog.classList.add('custom-size-guide-dialog--asd5000');
    }
    dialog.setAttribute('aria-labelledby', 'custom-size-guide-title');
    dialog.innerHTML = `
      <div class="custom-size-guide-dialog__inner">
        <button class="custom-size-guide-dialog__close" type="button" aria-label="Close size guide">&times;</button>
        <div class="custom-size-guide-dialog__header">
          <h2 class="custom-size-guide-dialog__title" id="custom-size-guide-title" style="font-family: Assistant, sans-serif;">SIZE GUIDE</h2>
          <div class="custom-size-guide-dialog__units" role="group" aria-label="Measurement unit">
            <button class="custom-size-guide-dialog__unit-button is-active" type="button" data-unit="inch" aria-pressed="true">INCH</button>
            <button class="custom-size-guide-dialog__unit-button" type="button" data-unit="cm" aria-pressed="false">CM</button>
          </div>
        </div>
        <div class="custom-size-guide-dialog__content"></div>
      </div>
    `;
    dialog.querySelector('.custom-size-guide-dialog__content').appendChild(copyChartContent(sizeChart));
    setupUnitToggle(dialog);
    loadMeasuringGuide(dialog);

    trigger.addEventListener('click', () => dialog.showModal());
    dialog.querySelector('.custom-size-guide-dialog__close').addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) dialog.close();
    });

    sizeLabel.appendChild(trigger);
    document.body.appendChild(dialog);
    return true;
  }

  // Retry for product forms whose option labels arrive after the deferred script.
  function initializeWithRetries() {
    [0, 250, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(() => initialize(), delay);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeWithRetries, { once: true });
  } else {
    initializeWithRetries();
  }
})();
